import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { User } from '../models/user.model';
import { notificationKeys } from '../utility/constants';
import { NavigationService } from './navigation.service';
import { UsersService } from './users.service';
import { WorkerTicketsService } from './worker-tickets.service';

@Injectable({
  providedIn: 'root',
})
export class PushNotificationsService {

  private currentToken: string;
  private user: User;

  constructor(
    private navigationService: NavigationService,
    private usersService: UsersService,
    private workerTicketsService: WorkerTicketsService,
  ) {
    this.addListeners();
  }

  private async addListeners() {
    if (Capacitor.isNativePlatform()) {
      await PushNotifications.addListener('registration', token => {
        console.info('Registration token: ', token.value);
        const tokenValue = token.value;
        this.currentToken = tokenValue;
        const user = this.user;
        if (!user?.notificationTokens) {
          user.notificationTokens = [];
        }
        if (!user?.notificationTokens.includes(tokenValue)) {
          user.notificationTokens.push(tokenValue);
          this.usersService.updateOnly(user.guid, { notificationTokens: user.notificationTokens });
        }
      });

      await PushNotifications.addListener('registrationError', err => {
        console.error('Registration error: ', err.error);
      });

      await PushNotifications.addListener('pushNotificationActionPerformed', notification => {
        const notificationData = notification.notification.data;
        const contents = notificationData.contents as string;
        if (contents) {
          const [key, guid] = contents.split('-');
          if ([notificationKeys.assign, notificationKeys.request].includes(key)) {
            this.navigationService.navigateToSite(guid);
          } else if (key === notificationKeys.feedbacks) {
            this.navigationService.navigateToLogs(key);
          } else if (key === notificationKeys.orders) {
            this.navigationService.navigateToOrders();
          } else if (key === notificationKeys.ticket) {
            const [ticketGuid, roomTicketGuid] = contents.substring(contents.indexOf('-') + 1).split('$');
            this.workerTicketsService.openRoomTicket(ticketGuid, roomTicketGuid);
          }
        }
      });
    }
  }

  public async registerNotifications(user: User) {
    if (Capacitor.isNativePlatform()) {
      this.user = user;
      let permStatus = await PushNotifications.checkPermissions();

      if (permStatus.receive === 'prompt') {
        permStatus = await PushNotifications.requestPermissions();
      }

      if (permStatus.receive !== 'granted') {
        throw new Error('User denied permissions!');
      }

      await PushNotifications.register();
    }
  }

  public async removeCurrentToken() {
    const user = this.usersService.currentUserS();
    if (user && this.currentToken) {
      user.notificationTokens = user.notificationTokens?.filter((token) => token !== this.currentToken) ?? [];
      return this.usersService.updateOnly(user.guid, { notificationTokens: user.notificationTokens }).then(() => {
        this.currentToken = undefined;
        this.user = undefined;
      });
    }
  }
}
